<template>
	<v-dialog v-model="visible" max-width="500" persistent>
		<v-card class="pb-6" @keyup="changeFocusInput">
			<div class="top-line"></div>
			<div class="pt-4 px-6 task-title">
				<div class="title-text">Create Timesheet</div>
				<div>
					<v-btn class="px-0 rounded-pill mr-2" max-height="24" disabled>
						<v-progress-linear v-if="!ts_for" :color="color" indeterminate></v-progress-linear>
						<v-user-avatar
							v-if="ts_for"
							size="24"
							class="text-caption text-decoration-none"
							:code="ts_for.display_name"
							:image="`/api/addressbook/${ts_for.id}/image.png`"
						/>
						<div v-if="ts_for" class="step-item-text text-capitalize ml-1 mr-3">{{ ts_for.display_name }}</div>
					</v-btn>
					<v-icon @click.stop="onClose()">mdi-close</v-icon>
				</div>
			</div>
			<v-alert dense class="mx-6 mt-3" type="error" v-if="error_msg.length !== 0">
				{{ errorMessageFormat(error_msg) }}
			</v-alert>
			<v-alert dense class="mx-6 mt-3" type="warning" v-if="warning_msg.length !== 0">
				{{ warning_msg }}
			</v-alert>
			<v-row class="mx-0 mt-4 mb-1 px-6" align="center">
				<TaskTimerIcon
					:recording="taskId === recording_task_id"
					:status_type="job_valid ? timesheet_status.type : 0" 
					:active="recording_task_id === 0"
					:inTable="false"
					:startText="`Start Timer`"
					:color="color"
					@start="startRecording(taskId)"
					@stop="stopRecording()"
				/>
			</v-row>
			<v-row class="mx-0 pl-6 pr-3">
				<v-col :cols="$vuetify.breakpoint.xsOnly ? 12:5" class="px-0" :class="$vuetify.breakpoint.xsOnly ? 'py-0':''">
					<div class="step-item-text">Duration</div>
					<v-text-field
						autocomplete="off"
						v-model="duration"
						:color="color"
						:error="error_duration_flag"
						background-color="#FAFAFA"
						:rules="rules"
						placeholder="eg, 10h 15m or 10:15"
						id="0"
						ref="input-0"
						autofocus
						outlined
						dense
						hide-details
						clearable
						@change="onDurationChange($event)"
					></v-text-field>
				</v-col>
				<v-col v-if="!$vuetify.breakpoint.xsOnly" :cols="1" align-self="center">
					<v-icon>mdi-swap-horizontal</v-icon>
				</v-col>
				<v-col :cols="$vuetify.breakpoint.xsOnly ? 12:6" class="pa-0">
					<DateTime
						id="1"
						ref="input-1"
						:refId="1"
						:color="color"
						:duration="duration"
						:startTimeInit="time_start_init"
						:endTimeInit="time_end_init"
						:timeOverage="is_beyond"
						:startError="start_time_error"
						:errorStartFlag="error_start_flag"
						:endError="end_time_error"
						:errorEndFlag="error_end_flag"
						@time_start="onTimeStartChange($event)"
						@time_end="onTimeEndChange($event)"
					/>
				</v-col>
			</v-row>
			<v-row class="mt-0 mb-3" :class="$vuetify.breakpoint.xsOnly ? 'pl-6 pr-3':'px-6'"  justify="space-between">
				<v-col :cols="12" class="py-0">
					<v-progress-linear v-if="loading_duration" class="mt-2" :color="color" indeterminate></v-progress-linear>
					<div v-if="!loading_duration" class="task-title">
						<div class="duration-text">
							<span :class="task_left.includes('-') ? 'duration-text-over':'duration-text-normal'" style="font-weight: 700">{{ task_left }}&nbsp;</span>
							<span class="duration-text-grey">left</span>
						</div>
						<div class="duration-text">
							<span class="duration-text-dark-grey" style="font-weight: 700">{{ task_actual }} / {{ task_budget }}&nbsp;</span>
							<span class="duration-text-grey">used</span>
						</div>
					</div>
				</v-col>
			</v-row>
			<v-row class="mt-4 mx-0 mb-0 px-6" align="center">
				<v-col :cols="$vuetify.breakpoint.xsOnly ? 12:6" class="pa-0">
					<div class="step-item-text">Date</div>
					<DatePicker
						:refId="`3`"
						:isOutlined="true" 
						:isIcon="true"
						:background="'#FAFAFA'"
						:color="color"
						:dateInit="timesheet_date"
						:suggestionText="''"
						@date="timesheet_date = ($event === null ? '' : $event)"
					/>
				</v-col>
				<v-col :cols="$vuetify.breakpoint.xsOnly ? 12:6" :class="$vuetify.breakpoint.xsOnly ? 'px-0 py-0':'pl-5 py-0'">
					<div class="step-item-text">Mark as</div>
					<v-btn-toggle v-model="ac_oc" :class="$vuetify.breakpoint.xsOnly ? 'mb-4':'mb-7'" borderless>
						<v-btn
							class="mr-2 rounded"
							:color="ac_oc == ac_type ? 'rgba(129, 204, 0, 0.25)':''"
							:value="ac_type"
							id="4"
							ref="input-4"
							depressed
							min-width="44" 
							max-width="44" 
							min-height="36" 
							max-height="36" 
						>
							AC
						</v-btn>
						<v-btn
							class="mr-5 rounded"
							:color="ac_oc == oc_type ? 'rgba(129, 204, 0, 0.25)':''"
							:value="oc_type"
							id="5"
							ref="input-5"
							depressed 
							min-width="44" 
							max-width="44" 
							min-height="36" 
							max-height="36" 
						>
							OC
						</v-btn>
					</v-btn-toggle>
				</v-col>
			</v-row>
			<div class="mt-1 mb-1 step-item-text px-6">Description</div>
			<v-textarea
				v-model="notes"
				style="width: 100%;"
				class="px-6"
				:rows="2"
				:color="color"
				id="6"
				ref="input-6"
				outlined
				dense
				hide-details
				clear-icon
				background-color="#FAFAFA"
			></v-textarea>
			<div class="px-6 mt-6" @click="checkDurationUnactive()">
				<v-btn
					id="7"
					ref="input-7"
					:disabled="!isActive"
					depressed
					block
					class="text-capitalize white--text" 
					:color="focus_key === 5 ? 'blue':'#00AAFD'"
					@click="checkDuration()"
				>
					<TimeSheet :color="`${isActive ? '#FFFFFF' : 'rgba(0, 0, 0, 0.26)' }`" class="mr-1" />
					Create Timesheet
				</v-btn>
			</div>
		</v-card>
		<DeleteConfirmDialog
			v-if="confirm_visible"
			:visible="confirm_visible" 
			:color="'error'"
			:title="`Timesheet data will be removed`" 
			:content="`Timesheet data will be removed if you exit this window. Are you sure you want to proceed?`"
			:cancel="`Go back`"
			:ok="'Okay, exit window'"
			:icon_name_title="'mdi-alert-circle'"
			:maxWidth="500"
			@cancel="confirm_visible = false"
			@ok="onCloseWindow()"
		/>
	</v-dialog>
</template>

<script>
import TimeSheet from "@/components/icons/timesheet";
import DatePicker from "@/components/date/date_picker.vue";
import DateTime from "@/components/date/date_time.vue";
import TaskTimerIcon from "@/components/tasks/task_timer_icon";
import DeleteConfirmDialog from "@/components/dialogs/delete_comfirm_dialog.vue";

import { validTime } from "@/common/validTime.js";
import { validHour } from "@/common/validHour.js";
import { budgetToMinutes, minutesToBudget } from "@/common/budgetToMinute.js";

import moment from 'moment';
import config from "@/config.js";

const JOr = 'JOr';
/** If selected the user will be able to enter and amend timesheets */
const TS = 'TS';

/** 0-'',1-AC,2-OC */
const AC_Type = 1;
const OC_Type = 2;

export default {
	props: {
		taskId: {
			type: Number,
			default: 0
		},
		employeesId: {
			type: Number,
			default: 0
		},
		visible: {
			type: Boolean,
			default: false
		},
		autoDuration: {
			type: Number,
			default: 0
		}
	},
	data() {
		this.ac_type = AC_Type;
		this.oc_type = OC_Type;

		return {
			error_msg: '',
			warning_msg: '',
			color: this.$attrs.color ?  this.$attrs.color : '#81CC00',
			loading_ts: false,
			loading_duration: true,

			ts_for: null,
			ts_for_items: [],

			task_budget_type: 0,
			task_left: '',
			task_budget: '',
			task_actual: '',

			timesheet_date: '',
			autoDuration_local: this.autoDuration,
			duration: this.autoDuration !== 0 ? minutesToBudget(this.autoDuration):'',
			menu_start_time: false,
			menu_end_time: false,
			time_start: this.autoDuration !== 0 ? moment().subtract(this.autoDuration, 'm').format('HH:mm'): null,
			time_end: this.autoDuration !== 0 ? moment().format('HH:mm'): null,
			time_start_init: '',
			time_end_init: '',

			job_invalid_status_ids: [],
			timesheet_status: {id: 0, type: 0},
			job_valid: true,
			notes: '',
			isActive: false,
			focus_key: 0,
			confirm_visible: false,

			ac_oc: null,

			start_time_error: false,
			end_time_error: false,

			error_start_flag: false,
			error_end_flag: false,
			error_duration_flag: false,
			button_click_flag: false,

			rules: [
				value => {
					return validHour(value);
				}
			]
		}
	},
	components: {
		DatePicker,
		TimeSheet,
		TaskTimerIcon,
		DeleteConfirmDialog,
		DateTime
	},
	watch: {
		duration() {
			if (this.duration === null) {
				this.duration = '';
			}
		},
		focus_key: {
			handler(value, oldValue) {
				if (value !== oldValue) {
					this.focusInput(value);
				}
			},
			immediate: true
		}
	},
	computed: {
		completedJobPrivilege() {
			let isCompletedJobTmp = false;
			if (this.$store.state.privileges && this.$store.state.privileges.length !== 0 && this.$store.state.privileges.includes(JOr)) {
				isCompletedJobTmp = true;
			}
			return isCompletedJobTmp
		},
		amendTimesheetPrivilege() {
			let isAmendTimesheetTpm = false;
			if (this.$store.state.privileges && this.$store.state.privileges.length !== 0 && this.$store.state.privileges.includes(TS)) {
				isAmendTimesheetTpm = true;
			}
			return isAmendTimesheetTpm;
		},
		recording_task_id: function() {
			return this.$store.state.recording.task_id;
		},
		recording_task_started: function() {
			return this.$store.state.recording.started;
		},
		is_beyond: function() {
			let is_beyond = false;

			if ((this.duration !== 0 || (typeof(this.duration) === 'string' && this.duration.length !== 0)) && this.time_start && this.time_start.length !== 0) {
				const start = moment(this.time_start, 'h:mm a');
				const end = moment('11:59 p.m', 'h:mm a');

				const diff = end.diff(start, 'minutes');
				const set_duration = budgetToMinutes(this.duration);
				if (set_duration > diff) {
					is_beyond = true;
				}
			}

			return is_beyond;
		}
	},
	async created() {
		await this.getJobStatusIds();
		await this.getAssignees();
		await this.loadTask(this.taskId);
	},
	methods: {
		getInput(id) {
			const input = this.$refs[`input-${id}`];
			return input;
		},
		focusInput(id) {
			const input = this.getInput(id);
			
			if (input) {
				if (id === 3 || id === 7 || id === 1) {
					input.$el.focus();
				} else {
					input.focus();
				}
			}
		},
		changeFocusInput(event) {
			if (event.target.id && event.target.id.length === 0) return;
			
			const key_code = {
				left: 37,
				up: 38,
				right: 39,
				down: 40,
				enter: 13,
				tab: 9
			};
			
			let item_key = this.focus_key;
			
			if (event.keyCode == key_code.tab) {
				item_key += 1;
			} else if (event.keyCode == key_code.enter) {
				this.isActive = this._isActiveCreateTimesheet();
				if (this.isActive && event.target.id !== '1' && event.target.id !== '4' && event.target.id !== '5') {
					this.checkDuration();
				}
			}

			if (this.getInput(item_key)) {
				this.focus_key = item_key;
			}
		},
		async getJobStatusIds() {
			const url = '/api/jobs/meta';
			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						const statuses_arr = data.substatusses;
						const job_invalid_statusses = statuses_arr.filter(s => { return !config.job_status_group_nomal.includes(s.group) });
						this.job_invalid_status_ids = job_invalid_statusses.map(s => { return s.id });
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		async getAssignees() {
			this.loading_ts = true;

			let url = '/api/tasks/list_assignees';

			const params = [];
			// 3 = "Employees", 12 = 'Freelancers', from 'api/addressbook/meta'
			params.push("abook_types=" + encodeURIComponent("3, 12"));

			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			await axios
				.get(url, {
					"Authorization": "Bearer " + this.$store.state.token,
					"Accept": "application/json"				
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						const ts_for_arr = data[0].employees;
						ts_for_arr.forEach(t => { t.display_name = this.formatName(t.display_name) })
						this.ts_for_items = data[0].employees;

						const addressbook = this.$store.state.user.addressbook;
						addressbook.display_name = this.formatName(addressbook.display_name);

						this.ts_for = addressbook;
						this.loading_ts = false;
					} else {
						this.loading_ts = false;
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.loading_ts = false;
					this.error_msg = e.message;
				});
		},
		async loadTask(task_id) {
			this.loading_duration = true;

			await axios
				.get("/api/tasks/" + task_id, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					let json = val.data;

					// For some reason axios isn't always converting our data
					if ((typeof json) == "string") {
						json = JSON.parse(json);
					}

					const { status, data, reason } = json;

					if (status == "OK") {
						if (this.completedJobPrivilege && this.job_invalid_status_ids.includes(data.job_type.id)) {
							this.job_valid = false;
						}
						
						this.timesheet_status = data.status;
						this.timesheet_date = moment().format('YYYY-MM-DD');

						const diff = data.budget - data.actual;
						this.task_left = `${diff >= 0 ? '':'- '}${minutesToBudget(Math.abs(diff))}`;
						this.task_budget = minutesToBudget(data.budget);
						this.task_actual = minutesToBudget(data.actual);
						this.task_budget_type = data.budget_type;

						if (data.kind && data.kind.id === null) {
							this.warning_msg = 'This task has no kind.'
						}

						if (this.autoDuration_local === 0 && data.start_time && data.end_time && data.start_time.length !== 0) {
							const time1 = moment(data.start_time, ['hh:mmA', 'hh:mm A', 'HH:mm']);
							const time2 = moment(data.end_time, ['hh:mmA', 'hh:mm A', 'HH:mm']);
							const budget = time2.diff(time1, 'minutes');


							const start_time_format = time1.format('h:mm a');
							const end_time_format = time2.format('h:mm a');
							const duration_tmp = minutesToBudget(budget);

							this.time_start = start_time_format;
							this.time_end = end_time_format;
							this.duration = duration_tmp;

							this.time_start_init = this.time_start ? this.time_start : '';
							this.time_end_init = this.time_end ? this.time_end : '';
						}

						if (this.autoDuration_local !== 0) {
							this.isActive = this._isActiveCreateTimesheet();
						}

						this.loading_duration = false;

					} else {
						this.loading_duration = false;
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.loading_duration = false;
					this.error_msg = e.message;
				});
		},
		checkDurationUnactive() {
			if (this.autoDuration_local === 0 && !this.isActive) {
				if (!this.time_start || this.time_start.length === 0) {
					this.error_start_flag = true;
				}
				if (!this.time_end || this.time_end.length === 0) {
					this.error_end_flag = true;
				}
				if (this.duration.length === 0 || !validHour(this.duration)) {
					this.error_duration_flag = true;
				}
				return;
			}
		},
		checkDuration() {
			const minutes = budgetToMinutes(this.duration);
			if (minutes > (24 * 60)) {
				this.error_msg = 'Over 24 hours limit';
				return;
			}

			let url = "/api/timesheets";

			const params = [];
			params.push("employees=" + this.ts_for.id);
			params.push("start_date=" + this.timesheet_date);
			params.push("end_date=" + this.timesheet_date);
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					let json = val.data;

					// For some reason axios isn't always converting our data
					if ((typeof json) == "string") {
						json = JSON.parse(json);
					}

					const { status, data, reason } = json;

					if (status == "OK") {
						let existed_minutes = 0;
						data.forEach(d => {
							existed_minutes += d.duration;
						});

						if (minutes + existed_minutes <= 24 * 60) {
							this.onCreateTimeSheet();	
						} else {
							this.error_msg = `Over 24 hours limit, ${existed_minutes} minute(s) existed`;
						}
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		startRecording(task_id) {
			this.$store.dispatch('start_recording', { task_id: task_id, started: new Date().getTime() });
			this.$emit('close');
		},
		stopRecording() {
			const new_time = new Date().getTime();
			const time_passed_in_ms = new_time - this.$store.state.recording.started;
			const time_passed_in_mins = Math.round(time_passed_in_ms / 60000.0);
			const total_time = time_passed_in_mins;

			if (total_time === 0) {
				this.$store.dispatch('stop_recording');
				this.$emit('close');
			} else {
				this.autoDuration_local = total_time;
				this.duration = minutesToBudget(this.autoDuration_local);
				this.time_start = moment(this.recording_task_started).format('h:mm a');
				this.time_end = moment(this.recording_task_started).add(this.autoDuration_local, 'm').format('h:mm a');

				this.time_start_init = this.time_start ? this.time_start : '';
				this.time_end_init = this.time_end ? this.time_end : '';

				this.isActive = this._isActiveCreateTimesheet();
			}
		},
		onCreateTimeSheet() {
			if (this.button_click_flag) return;

			const url = "/api/timesheets";

			const params_obj = {
				acoc: this.ac_oc !== AC_Type && this.ac_oc !== OC_Type ? 0 : this.ac_oc,
				date: moment(this.timesheet_date).format('YYYYMMDD'),
				duration: budgetToMinutes(this.duration),
				notes: this.notes,
				task_id: this.taskId
			};

			if (this.time_start && this.time_end) {
				params_obj['start_time'] = moment(this.time_start, 'h:mm a').format('HH:mm:ss');
				params_obj['end_time'] = 	moment(this.time_end, 'h:mm a').format('HH:mm:ss');
			}
			this.button_click_flag = true;

			axios
				.post(url, params_obj, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const json = val.data;

					const { status, reason } = json;

					if (status == "OK") {
						if (this.autoDuration_local !== 0) {
							this.$store.dispatch('stop_recording');
						}
						this.$emit('close');
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				})
				.finally(() => {
					this.button_click_flag = false;
				});
		},
		onDurationChange(duration) {
			this.checkError();

			if (validHour(duration)) {
				const duration_minute = budgetToMinutes(duration);
				this.duration = minutesToBudget(duration_minute);
			}

			this.isActive = this._isActiveCreateTimesheet();
		},
		onTimeStartChange(time) {
			this.start_time_error = false;
			this.end_time_error = false;

			this.time_start = time;

			this.checkError();
			this.onDuration(true);
			this.isActive = this._isActiveCreateTimesheet();
		},
		onTimeEndChange(time) {
			this.start_time_error = false;
			this.end_time_error = false;

			this.time_end = time;

			this.checkError();
			this.onDuration(false);
			this.isActive = this._isActiveCreateTimesheet();
		},
		checkError() {
			this.start_time_error = false;
			this.end_time_error = false;
			this.error_start_flag = false;
			this.error_end_flag = false;
			this.error_duration_flag = false;
		},
		onDuration(is_start=true) {
			const time_start = this.time_start ? this.time_start : '0';
			let time_start_tmp = time_start.replace(/\s+/g,"");

			const time_end = this.time_end ? this.time_end : '0';
			let time_end_tmp = time_end.replace(/\s+/g,"");

			if (validTime(time_start_tmp) && validTime(time_end_tmp)) {
				const time1 = moment(time_start_tmp, 'HH:mma');
				const time2 = moment(time_end_tmp, 'HH:mma');
				const budget = time2.diff(time1, 'minutes');

				if (budget === 0) {
					this.duration = '';
				}
				if (budget < 0) {
					if (is_start) {
						this.start_time_error = true;
					} else {
						this.end_time_error = true;
					}
				} 
				if (budget > 0) {
					this.duration = minutesToBudget(budget);
				}
			}
		},
		onClose() {
			if (this.taskId === this.recording_task_id && this.autoDuration_local !== 0) {
				this.confirm_visible = true;
			} else {
				this.$emit('close');
			}
		},
		onCloseWindow() {
			if (this.taskId === this.recording_task_id) {
				this.confirm_visible = false;
				this.$store.dispatch('stop_recording');
			}

			this.$emit('close');
		},
		_isActiveCreateTimesheet() {
			let isActive = false;
			if (this.timesheet_date.length !== 0 && this.duration.length !== 0 && validHour(this.duration) && !this.end_time_error && !this.start_time_error && !this.is_beyond && this.warning_msg.length === 0) {
				if (this.time_start && this.time_start.length !== 0 && this.time_end && this.time_end.length !== 0) {
					isActive = true;
				}
				if (this.time_start && this.time_start.length === 0 && this.time_end && this.time_end.length === 0) {
					isActive = true;
				}
				if (!this.time_start && !this.time_end) {
					isActive = true;
				}
			}
			return isActive;
		},
		formatName(name) {
			let display_name = '';
			if (name.includes(',')) {
				const name_arr = name.split(',');
				if (name_arr[1].includes('(')) {
					const name_tmp = name_arr[1];
					const index = name_tmp.indexOf('(');
					const first_name = name_tmp.substring(0, index-1).trim();
					const last_name = name_arr[0].trim();
					display_name = `${first_name} ${last_name} ${name_tmp.substring(index, name_tmp.length)}`;
				} else {
					const first_name = name_arr[1].trim();
					const last_name = name_arr[0].trim();
					display_name = `${first_name} ${last_name}`;
				}
			} else {
				display_name = name;
			}

			return display_name;
		},
		errorMessageFormat(msg) {
			if (msg.length !== 0) {
				this.tid = setTimeout(this._removeMsg, 5000);

				if (msg.includes(config.server_msge_flag)) {
					const msg_arr = msg.split(config.server_msge_flag);
					return msg_arr[1];
				} else {
					return msg;
				}				
			}
		},
		_removeMsg() {
			this.error_msg = '';
			this.warning_msg = '';
		},
	}
}
</script>
<style lang="scss" scoped>
.top-line {
  width: 100%;
  height: 4px;
  background: #81CC00;
  border-radius: 4px 4px 0px 0px;
}
.task-title {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
}
.title-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #81CC00;
}
.step-item-text {
	font-family: 'Roboto';
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.25px;
	color: #37495F;
}
.duration-text {
	font-family: 'Roboto';
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	letter-spacing: 0.4px;
}
.duration-text-over {
	color: #F45252;
}
.duration-text-normal {
	color: #609800;
}
.duration-text-grey {
	color: rgba(25, 42, 62, 0.5);
}
.duration-text-dark-grey {
	color: rgba(25, 42, 62, 0.7);
}
.optional-text {
	font-family: Roboto;
	font-style: normal;
	font-weight: normal;
	font-size: 12px;
	line-height: 20px;

	text-align: right;
	letter-spacing: 0.4px;
	color: #37495F;
}
.optional-text-red {
	font-family: Roboto;
	font-style: normal;
	font-weight: normal;
	font-size: 12px;

	color: #F45252;
}
.optional-text-red-keyword {
	text-decoration: underline;
	&:hover {
		cursor: pointer;
	}
}
.tree-wrap {
	height: 300px;
	overflow: auto;
}
.tree-wrap-container {
  height: 99%;
}
.for-item-text-hover {
	&:hover {
		cursor: pointer;
		background-color: rgba(25, 42, 62, 0.1);
	}
}
</style>
